import createBehavior from '@js/functions/createBehavior.js';
import ListProduct from '@components/list/product/list-product.twig';
import lockScroll from '@js/utils/lock-scroll.js';
import * as focusTrap from 'focus-trap';

const cartMiniBehavior = createBehavior(
  'cart-mini',
  {
    bindUI() {
      this.ui = {};
      this.ui.element = this.getChild('element');
      this.ui.toggle = this.getChild('toggle');
      this.ui.count = this.ui.toggle ? this.ui.toggle.querySelector('[data-cart-mini-count]') : null;
      this.ui.toggleLabel = this.ui.toggle ? this.ui.toggle.querySelector('[data-cart-mini-label]') : null;
      this.ui.close = this.getChild('close');
      this.ui.list = this.getChild('list');
      this.ui.expand = this.getChild('expand');
      this.ui.content = this.getChild('content');

      if (this.ui.element) {
        this.isOpen = this.ui.element.classList.contains(this.openKlass);
      }

      if (this.isOpen) {
        this.ui.expand.removeAttribute('inert');
      } else {
        this.ui.toggle.setAttribute('inert', 'inert');
        this.ui.expand.setAttribute('inert', 'inert');
      }
    },
    bindEvents() {
      if (this.ui.toggle) {
        this.ui.toggle.addEventListener('click', this.toggle, false);
      }

      if (this.ui.close) {
        this.ui.close.addEventListener('click', this.close, false);
      }

      // Add event listener when cart is updated.
      document.addEventListener('cart:updated', this.cartUpdatedHandler, false);

      // Add event listener before page enter.
      document.addEventListener('page:beforeEnter', this.pageBeforeEnterHandler, false);

      // Close cart when nav is opened
      document.addEventListener('nav:opened', this.close, false)

      // Add event listener for esc touch.
      document.addEventListener('keydown', this.keyboardHandler);
    },
    rebindUIEvents() {
      // Unbind events.
      this.unbindEvents();

      // Get barba container.
      this.barbaContainer = document.querySelector('[data-barba="container"]:last-child');

      // Get elements.
      this.ui.element = this.barbaContainer.querySelector('[data-cart-mini-element]');
      this.ui.toggle = this.barbaContainer.querySelector('[data-cart-mini-toggle]');
      this.ui.count = this.ui.toggle ? this.ui.toggle.querySelector('[data-cart-mini-count]') : null;
      this.ui.toggleLabel = this.ui.toggle ? this.ui.toggle.querySelector('[data-cart-mini-label]') : null;
      this.ui.close = this.barbaContainer.querySelector('[data-cart-mini-close]');
      this.ui.list = this.barbaContainer.querySelector('[data-cart-mini-list]');
      this.ui.expand = this.barbaContainer.querySelector('[data-cart-mini-expand]');
      this.ui.content = this.barbaContainer.querySelector('[data-cart-mini-content]');

      // Bind events.
      this.bindEvents();
    },
    updateToggleLabel() {
      if (!this.ui.toggle) {
        return;
      }

      this.ui.toggle.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
      this.updateToggleAccessibleText(this.getNumberOfTickets());
    },
    getNumberOfTickets() {
      let numberOfTickets = 0;

      if (this.cart.products && this.cart.products.length) {
        this.cart.products.forEach(product => {
          if (product.prices && product.prices.length) {
            product.prices.forEach(price => {
              if (price.quantity) {
                numberOfTickets += price.quantity;
              }
            });
          }
        });
      }

      return numberOfTickets;
    },
    updateToggleAccessibleText(numberOfTickets = 0) {
      if (this.ui.count) {
        this.ui.count.textContent = numberOfTickets;
      }

      if (!this.ui.toggleLabel || !this.ui.toggle) {
        return;
      }

      const itemLabel = numberOfTickets > 1
        ? this.ui.toggle.dataset.labelItemPlural
        : this.ui.toggle.dataset.labelItemSingular;
      const actionLabel = this.isOpen ? this.ui.toggle.dataset.labelClose : this.ui.toggle.dataset.labelOpen;

      this.ui.toggleLabel.textContent = `${numberOfTickets} ${itemLabel} - ${actionLabel}`;
    },
    getLocalStorage() {
      // Get cart from local storage.
      const cartFromLocalStorage = localStorage.getItem('cart');

      // If we don't have any local storage, return.
      if (!cartFromLocalStorage) return;

      // Get cart object.
      const cartObj = JSON.parse(cartFromLocalStorage);

      // If we have a validity and it's not expired.
      if (cartObj.validity && cartObj.validity >= Date.now() && cartObj.locale && cartObj.locale === document.documentElement.getAttribute('lang') && cartObj.products && cartObj.products.length) {

        // Save cart object.
        this.cart = cartObj;

        // Build cart mini.
        this.build();

        // Show cart.
        setTimeout(() => {
          this.show();
        }, 600);
      }
    },
    build(onlyLastProduct) {
      // Init items.
      let items = [];

      // If we want to show latest product added.
      if (onlyLastProduct && this.latestProductAdded) {
        items.push(this.latestProductAdded);
      } else if (this.cart.products && this.cart.products.length) {
        items = this.cart.products;
      }

      // Build list markup.

      const listMarkup = ListProduct({
        items: items.map(item => {
          if (item.image) {
            item.image = {
              ...item.image,
              sources: {
                cover: {
                  default: item?.image?.sources?.cover?.square,
                  ...item?.image?.sources?.cover,
                },
              },
              sizes: [{
                crop: 'square',
                breakpoints: [
                  {
                    name: 'small',
                    width: 320
                  },
                  {
                    name: 'medium',
                    width: 320
                  },
                  {
                    name: 'large',
                    width: 380
                  },
                  {
                    name: 'xlarge',
                    width: 432
                  },
                  {
                    name: 'xxlarge',
                    width: 540
                  }
                ]
              }],
              role: 'cover',
              lazy: false
            };
          }
          return item;
        }),
        for_label: this.options['for-label'],
        locale: document.documentElement.getAttribute('lang'),
        variation: 'condensed'
      });

      // Append markup.
      this.ui.list.innerHTML = listMarkup;

      // Emit custom event
      document.dispatchEvent(new CustomEvent('page:updated'));

      this.updateToggleAccessibleText(this.getNumberOfTickets());
    },
    show() {
      if (this.alreadyInit) {
        this.ui.element.classList.add('no-transition');
      }
      this.ui.element.classList.add(this.visibleKlass);
      this.ui.toggle.removeAttribute('inert');
      this.alreadyInit = true;
    },
    hide() {
      this.ui.element.classList.remove(this.visibleKlass);
      this.ui.toggle.setAttribute('inert', 'inert');
      this.ui.expand.setAttribute('inert', 'inert');
    },
    toggle(e) {
      // If there is a timer, remove it.
      if (this.timer) {
        clearTimeout(this.timer);
      }

      // If it's open.
      if (this.isOpen) {
        this.close('toggle');
      } else {
        this.open(true);
      }
    },
    open(clickOnToggle) {
      this.ui.expand.removeAttribute('inert');

      // Init onlyLastProduct variable.
      let onlyLastProduct = clickOnToggle ? false : true;

      // Build cart.
      this.build(onlyLastProduct);

      // Add/Remove just-added class
      if (onlyLastProduct) {
        this.ui.element.classList.add(this.justAddedKlass);
      } else {
        this.ui.element.classList.remove(this.justAddedKlass);
      }

      this.ui.element.classList.add(this.openKlass);
      this.isOpen = true;
      lockScroll.enable(this.transitionDuration);
      if (!this.focusTrap) {
        this.focusTrap = focusTrap.createFocusTrap(this.ui.element);
      }
      this.focusTrap.activate();

      // focus title or node if no title
      let focusTarget = this.node.querySelector('.cart-mini__title') || this.node;
      focusTarget.setAttribute('tabindex', '-1');
      focusTarget.focus({preventScroll: true});

      // Emit event
      document.dispatchEvent(new CustomEvent('miniCart:opened'));

      // If there is a timer, remove it.
      if (this.timer) {
        clearTimeout(this.timer);
      }

      // If it's only added product, add timer.
      if (onlyLastProduct) {
        this.timer = setTimeout(() => {
          this.close('autoclose');
        }, this.closeAfterDuration);
      }

      this.updateToggleLabel();
    },
    close(e) {
      this.ui.expand.setAttribute('inert', '');

      this.ui.element.classList.remove(this.openKlass);
      this.isOpen = false;

      // After it's closed
      setTimeout(() => {

        // Disable scroll.
        lockScroll.disable();

        // disable focus trap
        if (this.focusTrap) {
          this.focusTrap.deactivate();
        }

        this.ui.toggle.focus();

      }, this.transitionDuration);

      this.updateToggleLabel();
    },
    pageBeforeEnterHandler(e) {
      // Rebind new UI and events.
      this.rebindUIEvents();

      // Disable lock scroll.
      lockScroll.disable();

      // disable focus trap
      if (this.focusTrap) {
        this.focusTrap.deactivate();
        this.focusTrap = null;
      }

      // Reset isOpen page.
      this.isOpen = false;

      // If cart is valid and we have some products
      if (this.cart && this.cart.validity && this.cart.validity >= Date.now() && this.cart.products && this.cart.products.length) {
        this.build();
        this.show();
      } else {
        this.hide();
      }
    },
    cartUpdatedHandler(e) {
      // Get new cart object.
      this.cart = e.detail ? e.detail.cart : {};
      this.latestProductAdded = e.detail ? e.detail.latestProductAdded : null;

      // Build cart.
      this.build(true);

      // Check if we have some product.
      if (!this.cart || !this.cart.products || this.cart.products.length === 0) {
        this.hide();
      } else {
        // If it's not visible.
        if (!this.ui.element.classList.contains(this.visibleKlass)) {
          this.show();
          setTimeout(() => {
            if (e.detail.openCartMini) {
              this.open();
            }
          }, this.transitionDuration * 2);
        } else {
          if (e.detail.openCartMini) {
            this.open();
          }
        }
      }
    },
    keyboardHandler(e) {
      // If it's not open, return.
      if (!this.ui.element.classList.contains(this.openKlass)) return;

      // If we press escape.
      if (e.keyCode === 27) {
        this.close('escape');
      }
    },
    unbindEvents() {
      this.ui.toggle.removeEventListener('click', this.toggle);
      this.ui.close.removeEventListener('click', this.close);
      document.removeEventListener('cart:updated', this.cartUpdatedHandler);
      document.removeEventListener('page:beforeEnter', this.pageBeforeEnterHandler);
      document.removeEventListener('keydown', this.keyboardHandler);
    }
  },
  {
    init() {
      this.firstTimeAdded = true;
      this.visibleKlass = 'is-visible';
      this.openKlass = 'is-open';
      this.justAddedKlass = 'is-just-added';
      this.transitionDuration = 300;
      this.closeAfterDuration = 3000;
      this.isOpen = false;
      this.alreadyInit = false;

      this.cart = {};
      this.latestProductAdded = null;

      this.bindUI();
      this.bindEvents();
      this.getLocalStorage();
      this.updateToggleLabel();
    },
    destroy() {
      this.unbindEvents();
    }
  }
);

export default cartMiniBehavior;
